import React from "react";
import SettingsSection from "../SettingsSection";
import { DEFAULT_GIVEAWAY_STYLE } from "../../data/giveawayDefaults";

export default function GiveawaySection({
  giveawayStyle,
  setGiveawayStyle,
  embedColor,
  isOpen,
  setIsOpen,
  setHasUnsavedChanges,
}) {
  const style = { ...DEFAULT_GIVEAWAY_STYLE, ...giveawayStyle };
  const update = (field, value) =>
    setGiveawayStyle({ ...style, [field]: value });

  const useServerColor = !style.color;

  return (
    <SettingsSection>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "15px" }}
      >
        Customize the look of <code>/admin giveaway start</code> and the
        follow-up messages when it ends or is rerolled. Stylized / gothic text
        is fine - Discord will show whatever you paste here. Placeholders:{" "}
        <code>{"{prize}"}</code> <code>{"{host}"}</code>{" "}
        <code>{"{winners}"}</code> <code>{"{winnerList}"}</code>{" "}
        <code>{"{ends}"}</code> <code>{"{emoji}"}</code>{" "}
        <code>{"{requirements}"}</code>
      </p>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "15px" }}
      >
        Optional entry requirements on start: min level, messages (7 / 14 /
        all-time), invites, required role, must boost, account age, and server
        age. Optional bonus: <code>donor_extra_entry</code> gives site
        supporters a second ticket in the draw. If you omit{" "}
        <code>{"{requirements}"}</code> from the description,
        they are appended automatically when set. Invite totals start counting
        after the bot tracks joins (needs Manage Server / invite fetch).
      </p>

      <label className="input-label">ENTRY EMOJI</label>
      <input
        type="text"
        value={style.emoji}
        onChange={(e) => update("emoji", e.target.value)}
        className="settings-input"
        maxLength={80}
        placeholder="🎉 or :emoji_name:"
      />
      <p
        className="help-text"
        style={{ fontSize: "0.75rem", marginTop: "8px", opacity: 0.6 }}
      >
        Unicode emoji or a server emote shortcode like{" "}
        <code>{":heart_hop_black:"}</code>. In the description, use{" "}
        <code>{"{emoji}"}</code> by itself - do not wrap it in{" "}
        <code>{"< >"}</code>, bold, or code. People react with this to enter.
        Changing it does not affect giveaways that are already running.
      </p>

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          cursor: "pointer",
          margin: "16px 0 12px",
        }}
      >
        <input
          type="checkbox"
          checked={useServerColor}
          onChange={(e) =>
            update("color", e.target.checked ? "" : embedColor || "#4a0e17")
          }
          style={{ accentColor: "#b85c6e" }}
        />
        <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
          USE SERVER EMBED COLOR
        </span>
      </label>

      <label className="input-label">EMBED COLOR</label>
      <input
        type="color"
        value={style.color || embedColor || "#4a0e17"}
        onChange={(e) => update("color", e.target.value)}
        className="settings-input"
      />

      <label className="input-label" style={{ marginTop: "15px" }}>
        TITLE
      </label>
      <input
        type="text"
        value={style.title}
        onChange={(e) => update("title", e.target.value)}
        className="settings-input"
        maxLength={256}
      />

      <label className="input-label">DESCRIPTION</label>
      <textarea
        value={style.description}
        onChange={(e) => update("description", e.target.value)}
        className="settings-input large-text"
        rows={5}
        maxLength={4096}
      />

      <label className="input-label">FOOTER</label>
      <input
        type="text"
        value={style.footer}
        onChange={(e) => update("footer", e.target.value)}
        className="settings-input"
        maxLength={2048}
      />

      <p
        className="help-text"
        style={{
          color: "#7b6f62",
          fontSize: "0.8rem",
          margin: "22px 0 12px",
          fontWeight: "bold",
        }}
      >
        When the giveaway ends
      </p>

      <label className="input-label">ENDED TITLE</label>
      <input
        type="text"
        value={style.endedTitle}
        onChange={(e) => update("endedTitle", e.target.value)}
        className="settings-input"
        maxLength={256}
      />

      <label className="input-label">ENDED DESCRIPTION</label>
      <textarea
        value={style.endedDescription}
        onChange={(e) => update("endedDescription", e.target.value)}
        className="settings-input large-text"
        rows={3}
        maxLength={4096}
      />

      <label className="input-label">ENDED COLOR</label>
      <input
        type="color"
        value={style.endedColor || "#2b2d31"}
        onChange={(e) => update("endedColor", e.target.value)}
        className="settings-input"
      />

      <label className="input-label">WINNER ANNOUNCEMENT</label>
      <textarea
        value={style.winMessage}
        onChange={(e) => update("winMessage", e.target.value)}
        className="settings-input large-text"
        rows={3}
        maxLength={2000}
      />

      <label className="input-label">NO PARTICIPANTS MESSAGE</label>
      <textarea
        value={style.noWinnerMessage}
        onChange={(e) => update("noWinnerMessage", e.target.value)}
        className="settings-input large-text"
        rows={2}
        maxLength={2000}
      />

      <p
        className="help-text"
        style={{
          color: "#7b6f62",
          fontSize: "0.8rem",
          margin: "22px 0 12px",
          fontWeight: "bold",
        }}
      >
        Reroll
      </p>

      <label className="input-label">REROLL TITLE</label>
      <input
        type="text"
        value={style.rerollTitle}
        onChange={(e) => update("rerollTitle", e.target.value)}
        className="settings-input"
        maxLength={256}
      />

      <label className="input-label">REROLL COLOR</label>
      <input
        type="color"
        value={style.rerollColor || "#f1c40f"}
        onChange={(e) => update("rerollColor", e.target.value)}
        className="settings-input"
      />

      <label className="input-label">REROLL ANNOUNCEMENT</label>
      <textarea
        value={style.rerollMessage}
        onChange={(e) => update("rerollMessage", e.target.value)}
        className="settings-input large-text"
        rows={3}
        maxLength={2000}
      />

      <button
        type="button"
        className="manage-btn"
        style={{ marginTop: "18px" }}
        onClick={() => {
          setGiveawayStyle({ ...DEFAULT_GIVEAWAY_STYLE });
          if (setHasUnsavedChanges) setHasUnsavedChanges(true);
        }}
      >
        Reset to default look
      </button>
    </SettingsSection>
  );
}
